import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";
import React from "react";
import { RobotAvatarCanvas } from "./AlienAvatar";

export function GoogleAdsAvatar({
  className,
  style,
  render3d,
}: {
  className?: string;
  style?: React.CSSProperties;
  render3d?: boolean;
}) {
  if (render3d) {
    return <RobotAvatarCanvas variant="google" className={className} style={style} />;
  }

  return <GoogleCssAvatar className={className} style={style} />;
}

function GoogleCssAvatar({ className, style }: { className?: string; style?: React.CSSProperties }) {
  // Track raw mouse position relative to center
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Apply a spring for a bouncy, shaky, responsive feel
  const springConfig = { damping: 8, stiffness: 250, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Create aggressive 3D rotation effects based on smooth mouse position
  const rotateX = useTransform(smoothY, [-100, 100], [30, -30]);
  const rotateY = useTransform(smoothX, [-100, 100], [-30, 30]);
  
  // Create translation so the avatar leans towards the cursor
  const translateX = useTransform(smoothX, [-100, 100], [-10, 10]);
  const translateY = useTransform(smoothY, [-100, 100], [-10, 10]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate distance from center, capped to a range of -100 to 100
    const dx = Math.max(-100, Math.min(100, event.clientX - centerX));
    const dy = Math.max(-100, Math.min(100, event.clientY - centerY));
    
    mouseX.set(dx);
    mouseY.set(dy);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div 
      className={cn("relative w-full h-full flex items-center justify-center group", className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: 1000, ...style }}
    >
      {/* 
        Outer wrapper handles the dynamic following/shaking (x, y, rotateX, rotateY). 
      */}
      <motion.div
        className="relative w-full h-full preserve-3d cursor-pointer"
        style={{
          rotateX,
          rotateY,
          x: translateX,
          y: translateY,
          transformStyle: "preserve-3d",
        }}
      >
        {/* 
          Inner wrapper handles the idle hovering animation so it doesn't conflict 
          with the spring-driven y-translation.
        */}
        <motion.div
          className="relative w-full h-full"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Core Head Structure - Dark Metallic/Gunmetal */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-zinc-600 via-zinc-800 to-black shadow-[0_0_15px_rgba(0,0,0,0.8)] overflow-hidden">
            {/* Top highlight / metallic sheen */}
            <div className="absolute top-0 inset-x-0 h-[45%] bg-gradient-to-b from-white/20 to-transparent opacity-80 rounded-t-2xl" />
            
            {/* Inner robotic details */}
            <div className="absolute inset-[3px] rounded-[13px] border border-zinc-500/30 bg-zinc-900/90 backdrop-blur-sm" />
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-zinc-400/60" />
            
            {/* Chin plate */}
            <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/90 to-transparent rounded-b-2xl" />
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-black shadow-inner" />
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-zinc-600/50" />
            
            {/* Side plates / Ear modules */}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-4 bg-zinc-400/80 rounded-r-sm shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-4 bg-zinc-400/80 rounded-l-sm shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            
            {/* Antenna */}
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-2 bg-zinc-500 rounded-t-sm" />
            <motion.div 
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#4285F4] rounded-full shadow-[0_0_8px_rgba(66,133,244,1)]"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>

          {/* Floating Visor (Eyes) - Parallax pushed further out for deeper 3D tracking */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            style={{ transform: "translateZ(20px)" }} // Increased parallax
          >
            {/* Intense background glow behind the visor */}
            <div className="absolute inset-x-0 top-[30%] h-[40%] bg-blue-500/20 rounded-lg blur-md" />
            <div className="absolute inset-x-1 top-[35%] h-[30%] bg-black/60 rounded-lg blur-[2px]" />
            
            {/* The Google Logo as glowing eyes */}
            <motion.svg
              viewBox="0 0 24 24"
              className="w-[76%] h-auto relative z-10"
              style={{ filter: "drop-shadow(0 0 8px rgba(66,133,244,0.6))" }}
              xmlns="http://www.w3.org/2000/svg"
              initial={{ opacity: 0.9 }}
              animate={{ opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <title>Google Ads</title>
              <path
                d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.232c1.891-1.742 2.981-4.305 2.981-7.351z"
                fill="#4285F4"
              />
              <path
                d="M12 22c2.7 0 4.964-.895 6.619-2.422l-3.232-2.51c-.895.6-2.04.955-3.387.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.996 9.996 0 0 0 12 22z"
                fill="#34A853"
              />
              <path
                d="M6.405 13.9A6.013 6.013 0 0 1 6.09 12c0-.659.114-1.3.314-1.9V7.51H3.064A9.996 9.996 0 0 0 2 12c0 1.614.386 3.14 1.064 4.49l3.341-2.59z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.977c1.468 0 2.787.505 3.823 1.495l2.868-2.868C16.96 2.99 14.696 2 12 2 8.09 2 4.708 4.245 3.064 7.51l3.34 2.59C7.19 7.736 9.396 5.977 12 5.977z"
                fill="#EA4335"
              />
            </motion.svg>
          </motion.div>
          
          {/* Animated scanning line - enhanced to match new eyes */}
          <motion.div 
            className="absolute inset-x-0 h-[2px] bg-[#4285F4]/60 blur-[1px] shadow-[0_0_8px_rgba(66,133,244,0.8)]"
            animate={{ top: ["15%", "85%", "15%"] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            style={{ transform: "translateZ(5px)" }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
